<template>
  <public-layout
    badge="Find Talent"
    title="Professional Services"
    subtitle="Connect with skilled professionals across construction, real estate, architecture, and more."
  >
    <!-- ═══════ SERVICE LISTINGS ═══════ -->
    <section class="sc-section">
      <div class="sc-container">

        <!-- Not logged in -->
        <div v-if="!isLoggedIn" class="sc-cta-box">
          <div class="sc-cta-box__icon">🔧</div>
          <h2>Personalised Services, Just for You</h2>
          <p>Log in to see services matched to your profession and interests in the construction and real estate industry.</p>
          <div class="sc-cta-box__actions">
            <router-link to="/sign-in" class="btn-primary">Log in to view services</router-link>
            <router-link to="/register" class="btn-outline">Create free account</router-link>
          </div>
        </div>

        <!-- Logged in, loading -->
        <template v-else>
          <div class="sc-section-header">
            <div>
              <h2>Services for You</h2>
              <p>Opportunities matched to your professional profile</p>
            </div>
          </div>

          <div v-if="loading" class="services-grid">
            <div v-for="n in 6" :key="n" class="service-skeleton">
              <div class="skeleton" style="height:14px;width:80px;border-radius:20px;margin-bottom:.75rem;"></div>
              <div class="skeleton" style="height:22px;width:90%;border-radius:4px;margin-bottom:.5rem;"></div>
              <div class="skeleton" style="height:14px;width:70%;border-radius:4px;margin-bottom:.5rem;"></div>
              <div class="skeleton" style="height:14px;width:50%;border-radius:4px;margin-bottom:1.5rem;"></div>
              <div class="skeleton" style="height:36px;width:120px;border-radius:20px;"></div>
            </div>
          </div>

          <div v-else-if="services.length" class="services-grid">
            <router-link
              v-for="(svc, i) in services"
              :key="i"
              :to="'/service/' + svc.slug"
              class="service-card"
            >
              <div class="service-card__top">
                <span class="svc-badge">{{ svc.service }}</span>
                <span class="svc-date">{{ formatDate(svc.created_at) }}</span>
              </div>
              <h3>{{ svc.service }}</h3>
              <p v-html="truncate(svc.description, 160)"></p>
              <div class="service-card__footer">
                <span class="btn-outline service-card__btn">View Details</span>
              </div>
            </router-link>
          </div>

          <div v-else class="sc-empty-state">
            <div class="sc-empty-state__icon">🔍</div>
            <h3>No services found</h3>
            <p>We couldn't find services matching your profile right now. Check back soon.</p>
          </div>

          <div v-if="lastPage > 1" class="blog-pagination">
            <button class="page-btn" :disabled="currentPage === 1" @click="fetchData(prevUrl)">&#8592; Previous</button>
            <span class="page-info">Page {{ currentPage }} of {{ lastPage }}</span>
            <button class="page-btn" :disabled="currentPage === lastPage" @click="fetchData(nextUrl)">Next &#8594;</button>
          </div>
        </template>

      </div>
    </section>

    <!-- ═══════ HOW IT WORKS ═══════ -->
    <section class="sc-section sc-how-services">
      <div class="sc-container">
        <div class="sc-section-header" style="justify-content:center;text-align:center;">
          <div><h2>How Services Work</h2><p>Four simple steps to find or offer professional services</p></div>
        </div>
        <div class="how-grid">
          <div class="how-step">
            <div class="how-num">01</div>
            <h3>Browse Opportunities</h3>
            <p>Explore services posted by companies and individuals across Africa's construction &amp; real estate sector.</p>
          </div>
          <div class="how-step">
            <div class="how-num">02</div>
            <h3>Apply with Your Profile</h3>
            <p>Your Structurecity profile acts as your portfolio. Apply directly with one click and stand out.</p>
          </div>
          <div class="how-step">
            <div class="how-num">03</div>
            <h3>Get Hired &amp; Collaborate</h3>
            <p>Connect with clients, agree on terms, and build your professional reputation on the platform.</p>
          </div>
          <div class="how-step">
            <div class="how-num">04</div>
            <h3>Grow Your Reputation</h3>
            <p>Earn reviews, build a track record of successful projects, and become a go-to professional in your field.</p>
          </div>
        </div>
      </div>
    </section>
  </public-layout>
</template>

<script>
import axios from 'axios';
import moment from 'moment';
import PublicLayout from '@/layouts/PublicLayout.vue';
import { useSEO, SITE_ORIGIN } from '@/utils/seo';

export default {
  name: 'Services',
  components: { PublicLayout },

  data() {
    return {
      services: [],
      loading: true,
      currentPage: 1,
      lastPage: 1,
      prevUrl: null,
      nextUrl: null,
    };
  },

  computed: {
    isLoggedIn() { return !!localStorage.getItem('user'); },
  },

  created() {
    useSEO({
      title: 'Construction & Real Estate Services',
      description: 'Find top-rated construction and real estate services on Structurecity — architecture, engineering, property management, interior design and more across Africa.',
      url: SITE_ORIGIN + '/services',
    });
    if (this.isLoggedIn) this.fetchData();
    else this.loading = false;
  },

  methods: {
    fetchData(url = process.env.VUE_APP_API_URL + 'service/all/services/' + localStorage.getItem('user')) {
      this.loading = true;
      axios(url)
        .then(res => {
          if (res.status === 200) {
            this.services    = res.data.data || [];
            this.currentPage = res.data.current_page || 1;
            this.lastPage    = res.data.last_page || 1;
            this.prevUrl     = res.data.prev_page_url;
            this.nextUrl     = res.data.next_page_url;
          }
        })
        .catch(() => {})
        .finally(() => { this.loading = false; });
    },

    formatDate(d) { return moment(d).format('MMM DD, YYYY'); },
    truncate(html, len) {
      const text = html ? html.replace(/<[^>]*>/g, '') : '';
      return text.length > len ? text.substring(0, len) + '…' : text;
    },
  },
};
</script>

<style scoped>
/* Services grid */
.services-grid {
  display: grid; grid-template-columns: repeat(auto-fill, minmax(300px, 1fr)); gap: 1.5rem;
}

.service-card {
  background: var(--surface); border-radius: var(--radius-lg);
  padding: 1.75rem; border: 1px solid var(--border-light); box-shadow: var(--shadow-sm);
  text-decoration: none; color: inherit; display: flex; flex-direction: column; gap: .75rem;
  transition: transform var(--transition), box-shadow var(--transition), border-color var(--transition);
}
.service-card:hover { transform: translateY(-4px); box-shadow: var(--shadow-lg); border-color: var(--border); text-decoration: none; color: inherit; }

.service-card__top { display: flex; align-items: center; justify-content: space-between; }
.svc-badge {
  display: inline-block; padding: .25rem .75rem;
  background: rgba(30,58,95,.08); color: var(--primary);
  border-radius: var(--radius-full); font-size: .78rem; font-weight: 600;
}
.svc-date { font-size: .8125rem; color: var(--text-light); }

.service-card h3 { font-size: 1.0625rem; font-weight: 700; color: var(--text); margin: 0; line-height: 1.3; }
.service-card p { font-size: .9rem; color: var(--text-muted); line-height: 1.6; margin: 0; flex: 1; }

.service-card__footer { margin-top: auto; padding-top: 1rem; border-top: 1px solid var(--border-light); }
.service-card__btn { font-size: .875rem; padding: .4rem 1rem; }

/* CTA box */
.sc-cta-box {
  text-align: center; max-width: 560px; margin: 0 auto; padding: 4rem 2rem;
}
.sc-cta-box__icon { font-size: 3.5rem; margin-bottom: 1.25rem; }
.sc-cta-box h2 { font-size: 1.75rem; font-weight: 800; color: var(--text); margin: 0 0 .75rem; letter-spacing: -.02em; }
.sc-cta-box p { color: var(--text-muted); font-size: 1rem; line-height: 1.65; margin: 0 0 2rem; }
.sc-cta-box__actions { display: flex; gap: 1rem; justify-content: center; flex-wrap: wrap; }

/* How it works */
.sc-how-services { background: var(--surface-2); }
.how-grid { display: grid; grid-template-columns: repeat(auto-fill, minmax(260px, 1fr)); gap: 2rem; }
.how-step { text-align: center; padding: 2rem 1.5rem; }
.how-num { font-size: 3rem; font-weight: 900; color: var(--accent); opacity: .3; line-height: 1; margin-bottom: 1rem; letter-spacing: -.04em; }
.how-step h3 { font-size: 1.125rem; font-weight: 700; margin: 0 0 .625rem; color: var(--text); }
.how-step p { color: var(--text-muted); font-size: .9375rem; line-height: 1.65; margin: 0; }

/* Skeleton */
.service-skeleton { background: var(--surface); border-radius: var(--radius-lg); padding: 1.75rem; box-shadow: var(--shadow-sm); }
.skeleton { background: linear-gradient(90deg, var(--border-light) 25%, var(--surface-3) 50%, var(--border-light) 75%); background-size: 200% 100%; animation: shimmer 1.4s ease infinite; }
@keyframes shimmer { to { background-position: -200% 0; } }

/* Pagination */
.blog-pagination { display: flex; align-items: center; justify-content: center; gap: 1.5rem; margin-top: 3rem; }
.page-btn { padding: .5rem 1.25rem; border: 2px solid var(--border); border-radius: var(--radius-full); background: var(--surface); color: var(--text); font-family: var(--font); font-size: .9rem; font-weight: 500; cursor: pointer; transition: border-color .15s, background .15s; }
.page-btn:hover:not(:disabled) { border-color: var(--primary); color: var(--primary); }
.page-btn:disabled { opacity: .4; cursor: not-allowed; }
.page-info { font-size: .9rem; color: var(--text-muted); }

/* Empty */
.sc-empty-state { text-align: center; padding: 4rem 2rem; }
.sc-empty-state__icon { font-size: 3rem; margin-bottom: 1rem; }
.sc-empty-state h3 { font-size: 1.25rem; font-weight: 700; color: var(--text); margin: 0 0 .5rem; }
.sc-empty-state p { color: var(--text-muted); margin: 0; }
</style>
